import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfaceOverrideEntity } from '@unifyapps/defs/types/interface/interfaceOverride';
import set from 'lodash/set';
import toPath from 'lodash/toPath';
import { create } from 'mutative';
import { FORBIDDEN_PATH_SEGMENTS } from './constants';

function isPathSafe(path: string): boolean {
  // toPath parses dot AND bracket notation, so the proto guard cannot be bracket-bypassed
  return !toPath(path).some((segment) => FORBIDDEN_PATH_SEGMENTS.has(segment));
}

// release-survival that still allows ADD: a missing ancestor is fine (set creates it); skip only when
// an EXISTING ancestor was reshaped to a non-object/array, which lodash.set would corrupt.
function isSafeToSet(properties: Record<string, unknown>, path: string): boolean {
  const segments = toPath(path);
  let node: unknown = properties;
  for (let index = 0; index < segments.length - 1; index += 1) {
    if (node === undefined || node === null) return true;
    const next = (node as Record<string, unknown>)[segments[index]];
    const nextExists = next !== undefined && next !== null;
    if (nextExists && (typeof next !== 'object' || Array.isArray(next))) return false;
    node = next;
  }
  return true;
}

export function applyInterfaceOverride(
  record: InterfaceEntity,
  override: InterfaceOverrideEntity | undefined,
): InterfaceEntity {
  try {
    const entries = override?.properties.overrides;
    if (!Array.isArray(entries)) return record; // overrides is an ordered array of { path, value }

    const mergedProperties = create(record.properties as Record<string, unknown>, (draft) => {
      for (const entry of entries) {
        const { path, value } = entry ?? {};
        if (typeof path !== 'string') continue;
        if (!isPathSafe(path)) continue;
        if (!isSafeToSet(draft, path)) continue;
        set(draft, path, value);
      }
    });

    // mutative returns the same reference on a true no-op → keeps the record memo-stable
    if (mergedProperties === record.properties) return record;
    return { ...record, properties: mergedProperties } as InterfaceEntity;
  } catch {
    // fail-open: a bad override must never take down an app whose base interface loaded fine
    return record;
  }
}
